import { OTLayout } from "@/components/layout/ot-layout";
import { useState, useEffect } from "react";
import { Users, ChevronDown, ChevronUp, ExternalLink, GraduationCap } from "lucide-react";
import { Link } from "wouter";

const SS_COMPETENCY_AREAS = [
  {
    title: "Occupational Performance in Rheumatology",
    content: "Assessment and promotion of occupational performance across self-care, productivity, and leisure for people with rheumatic conditions. Includes COPM, ABILHAND, and HAQ-DI application.",
  },
  {
    title: "Joint Protection and Energy Conservation",
    content: "Delivery of evidence-based joint protection programmes (including JPPE) and fatigue management strategies. Includes group programme facilitation and outcome monitoring.",
  },
  {
    title: "Hand Therapy and Splinting",
    content: "Specialist assessment and fabrication of therapeutic splints for rheumatic hand conditions. Includes resting splints, working splints, and post-surgical orthoses.",
  },
  {
    title: "Assistive Technology and Environmental Adaptation",
    content: "Prescription, training, and follow-up for assistive devices and home adaptations. Includes DFG referral, Living Made Easy, and community OT liaison.",
  },
  {
    title: "Psychological Wellbeing and Self-Management",
    content: "Addressing the psychological impact of rheumatic disease on occupational participation. Includes pain management, cognitive adaptation, and self-management programme facilitation.",
  },
  {
    title: "Work Participation and Vocational Rehabilitation",
    content: "Supporting employment participation and work-related goals for people with rheumatic conditions. Includes workplace assessment, Fit Note guidance, and Access to Work referral.",
  },
];

const POSITION_STATEMENTS = [
  {
    title: "OT and Self-Management in Rheumatology",
    summary: "RCOT SS Rheumatology supports group-based self-management programmes as a core component of rheumatology OT practice, aligned with NICE musculoskeletal guidelines.",
  },
  {
    title: "Joint Protection Programme (JPPE)",
    summary: "The Joint Protection Programme for Early Rheumatoid Arthritis (JPPE) is the evidence-based OT standard for RA self-management education, validated in NICE RA guidance.",
  },
  {
    title: "Fatigue Management in Inflammatory Arthritis",
    summary: "RCOT endorses the COT-developed fatigue management programme. OTs are well-placed to lead fatigue assessment and intervention as part of the rheumatology MDT.",
  },
  {
    title: "OT in Paediatric Rheumatology",
    summary: "RCOT SS Rheumatology recognises the distinct role of OTs in paediatric rheumatology, including school participation, play, and transition to adult services.",
  },
];

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  category: string;
  hours: number;
}

export default function OtSpecialistSection() {
  const [openSection, setOpenSection] = useState<number | null>(null);
  const [openStatement, setOpenStatement] = useState<number | null>(null);
  const [activities, setActivities] = useState<CpdActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  useEffect(() => {
    const year = new Date().getFullYear();
    setLoadingActivities(true);
    fetch(`/api/ot/cpd?year=${year}`)
      .then(r => r.ok ? r.json() : [])
      .then((data: CpdActivity[]) => {
        setActivities(data.filter(a => a.category === "professional" || a.category === "educational"));
      })
      .finally(() => setLoadingActivities(false));
  }, []);

  return (
    <OTLayout title="RCOT SS Rheumatology Hub">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-700 to-amber-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">RCOT Specialist Section — Rheumatology</h2>
              <p className="text-amber-200 text-sm">Professional community for rheumatology occupational therapists</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            The RCOT Specialist Section in Rheumatology (SS Rheumatology) is the professional community for OTs working in rheumatology across the UK. Membership provides access to specialist CPD, position statements, and a network of rheumatology OT colleagues.
          </p>
        </div>

        {/* Membership Information */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Membership Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-800 text-sm mb-2">Who can join?</h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                All RCOT members working in or with an interest in rheumatology occupational therapy. This includes OTs in NHS, community, and private practice, as well as students and those in related roles.
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-800 text-sm mb-2">Member benefits</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Access to RCOT SS Rheumatology newsletter and updates</li>
                <li>• Specialist study days and CPD events</li>
                <li>• Rheumatology OT clinical resources and position statements</li>
                <li>• Networking with UK rheumatology OT colleagues</li>
                <li>• Input to national guideline consultations</li>
              </ul>
            </div>
          </div>
          <a
            href="https://www.rcot.co.uk/about-occupational-therapy/specialist-sections/rheumatology"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-sm text-amber-700 hover:text-amber-800 font-medium"
          >
            <ExternalLink className="h-4 w-4" />
            Visit RCOT SS Rheumatology website
          </a>
        </div>

        {/* Specialist Competencies */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Specialist Competency Areas</h3>
          <p className="text-sm text-slate-500 mb-4">Key competency areas for rheumatology OT practice, aligned with RCOT SS Rheumatology guidance and NICE musculoskeletal pathways.</p>
          <div className="space-y-2">
            {SS_COMPETENCY_AREAS.map((area, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenSection(openSection === i ? null : i)}
                  className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-medium text-slate-800">{area.title}</span>
                  {openSection === i ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {openSection === i && (
                  <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {area.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Clinical Standards and Position Statements */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Clinical Standards & Position Statements</h3>
          <div className="space-y-2">
            {POSITION_STATEMENTS.map((ps, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenStatement(openStatement === i ? null : i)}
                  className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-medium text-slate-800">{ps.title}</span>
                  {openStatement === i ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {openStatement === i && (
                  <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {ps.summary}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* SS Rheumatology CPD Log */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-amber-600" />
              <h3 className="font-bold text-slate-900">SS Rheumatology CPD Activities This Year</h3>
            </div>
            <Link href="/ot/cpd">
              <button className="text-sm text-amber-700 hover:text-amber-800 font-medium">Log new activity →</button>
            </Link>
          </div>
          {loadingActivities ? (
            <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-12 bg-slate-100 animate-pulse rounded-xl" />)}</div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <GraduationCap className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No professional or educational CPD activities logged this year.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activities.slice(0, 8).map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{a.title}</p>
                    <p className="text-xs text-slate-400">{new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {a.category}</p>
                  </div>
                  <span className="text-sm font-medium text-amber-700">{a.hours} hrs</span>
                </div>
              ))}
              {activities.length > 8 && (
                <Link href="/ot/cpd">
                  <p className="text-center text-xs text-amber-700 hover:text-amber-800 cursor-pointer pt-2">View all {activities.length} activities →</p>
                </Link>
              )}
            </div>
          )}
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>RCOT SS Rheumatology:</strong> Information based on publicly available RCOT Specialist Section resources. For the most current membership information, events, and position statements, visit the official RCOT SS Rheumatology pages at rcot.co.uk.
        </div>
      </div>
    </OTLayout>
  );
}
